import React, { useState } from 'react';
import { GitCompareIcon, SearchIcon, FilterIcon, DownloadIcon, CheckCircle2Icon, AlertCircleIcon, SparklesIcon, ArrowRightIcon, FileTextIcon, CreditCardIcon } from 'lucide-react';
import { mockSuggestions, mockExceptions, mockKpis, RecoSuggestion } from '../mocks/reconciliationData';
import { formatMoney } from '../../../shared/config/localeProfile';
import { ReconciliationDrawer } from '../components/ReconciliationDrawer';
export function Reconciliation() {
  const [activeTab, setActiveTab] = useState<'matches' | 'exceptions'>('matches');
  const [streamFilter, setStreamFilter] = useState<'all' | 'ar' | 'ap'>('all');
  const [selectedSuggestion, setSelectedSuggestion] = useState<RecoSuggestion | null>(null);
  const [suggestions, setSuggestions] = useState(mockSuggestions);
  const handleAccept = (id: string) => {
    setSuggestions(prev => prev.map(s => s.id === id ? {
      ...s,
      status: 'matched'
    } : s));
    setSelectedSuggestion(null);
  };
  const handleAcceptAllHighConfidence = () => {
    setSuggestions(prev => prev.map(s => s.confidence === 'high' && s.status === 'suggested' ? {
      ...s,
      status: 'matched'
    } : s));
  };
  const filteredSuggestions = suggestions.filter(s => {
    if (streamFilter === 'ar') return s.type === 'receivable';
    if (streamFilter === 'ap') return s.type === 'payable';
    return true;
  });
  const getConfidenceColor = (score?: number) => {
    if (!score) return 'bg-gray-100 text-gray-800';
    if (score >= 90) return 'bg-green-100 text-green-800';
    if (score >= 70) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'matched':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-8702">
            Matched
          </span>;
      case 'suggested':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-8703">
            <SparklesIcon size={12} className="mr-1" data-id="element-8704" /> Suggested
          </span>;
      case 'partial':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-id="element-8705">
            Partial
          </span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-8706">
            {status}
          </span>;
    }
  };
  return <div className="max-w-[1600px] mx-auto space-y-6 pb-12" data-id="element-8707">
      {/* Header & KPIs */}
      <div className="flex flex-col gap-6" data-id="element-8708">
        <div className="flex items-center justify-between" data-id="element-8709">
          <div data-id="element-8710">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-8711">
              <GitCompareIcon className="text-gray-400" data-id="element-8712" />
              Reconciliation
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-8713">
              Auto-match money movement against commercial documents.
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-8714">
            <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-8715">
              <DownloadIcon size={16} data-id="element-8716" />
              Export Journal
            </button>
            <button onClick={handleAcceptAllHighConfidence} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2" data-id="element-8717">
              <CheckCircle2Icon size={16} data-id="element-8718" />
              Accept High Confidence
            </button>
          </div>
        </div>

        {/* KPI Band */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-8719">
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-8720">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8721">
              % Reconciled (Count)
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-8722">
              {mockKpis.reconciledCountPct}%
            </div>
            <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3" data-id="element-8723">
              <div className="bg-green-500 h-1.5 rounded-full" style={{
              width: `${mockKpis.reconciledCountPct}%`
            }} data-id="element-8724"></div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-8725">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8726">
              % Reconciled (Value)
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-8727">
              {mockKpis.reconciledValuePct}%
            </div>
            <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3" data-id="element-8728">
              <div className="bg-green-500 h-1.5 rounded-full" style={{
              width: `${mockKpis.reconciledValuePct}%`
            }} data-id="element-8729"></div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-8730">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8731">
              Unmatched Value
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-8732">
              {formatMoney(mockKpis.unmatchedValue)}
            </div>
            <div className="mt-2 text-xs text-amber-600 font-medium" data-id="element-8733">
              Requires attention
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-8734">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8735">
              Exceptions
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-8736">
              {mockKpis.exceptionsCount}
            </div>
            <div className="mt-2 text-xs text-red-600 font-medium" data-id="element-8737">
              Action required
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-8738">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8739">
              AI Auto-Matched
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-8740">
              {formatMoney(mockKpis.autoMatchedValue)}
            </div>
            <div className="mt-2 text-xs text-blue-600 font-medium flex items-center gap-1" data-id="element-8741">
              <SparklesIcon size={12} data-id="element-8742" /> Saved 14 hours
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-8743">
        <div className="flex items-center gap-4" data-id="element-8744">
          <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-8745">
            <button onClick={() => setActiveTab('matches')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'matches' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-8746">
              Matching Workspace
            </button>
            <button onClick={() => setActiveTab('exceptions')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${activeTab === 'exceptions' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-8747">
              Exceptions
              <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs" data-id="element-8748">
                {mockExceptions.length}
              </span>
            </button>
          </div>

          {activeTab === 'matches' && <div className="h-6 w-px bg-gray-300" data-id="element-8749"></div>}

          {activeTab === 'matches' && <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-8750">
              <button onClick={() => setStreamFilter('all')} className={`px-3 py-1 text-xs font-medium rounded-md ${streamFilter === 'all' ? 'bg-white shadow-sm' : 'text-gray-500'}`} data-id="element-8751">
                All Streams
              </button>
              <button onClick={() => setStreamFilter('ar')} className={`px-3 py-1 text-xs font-medium rounded-md ${streamFilter === 'ar' ? 'bg-white shadow-sm' : 'text-gray-500'}`} data-id="element-8752">
                AR (Receivables)
              </button>
              <button onClick={() => setStreamFilter('ap')} className={`px-3 py-1 text-xs font-medium rounded-md ${streamFilter === 'ap' ? 'bg-white shadow-sm' : 'text-gray-500'}`} data-id="element-8753">
                AP (Payables)
              </button>
            </div>}
        </div>

        <div className="flex items-center gap-3" data-id="element-8754">
          <div className="relative" data-id="element-8755">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-8756" />
            <input type="text" placeholder="Search reference, amount..." className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 w-64" data-id="element-8757" />
          </div>
          <button className="p-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50" data-id="element-8758">
            <FilterIcon size={16} data-id="element-8759" />
          </button>
        </div>
      </div>

      {/* Main Workspace */}
      {activeTab === 'matches' && <div className="space-y-4" data-id="element-8760">
          {filteredSuggestions.map(suggestion => <div key={suggestion.id} onClick={() => setSelectedSuggestion(suggestion)} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:border-blue-300 hover:shadow-md transition-all cursor-pointer overflow-hidden" data-id="element-8761">
              <div className="flex flex-col lg:flex-row" data-id="element-8762">
                {/* Left: Payments */}
                <div className="flex-1 p-5 bg-gray-50/50 border-b lg:border-b-0 lg:border-r border-gray-200" data-id="element-8763">
                  <div className="flex items-center justify-between mb-4" data-id="element-8764">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-8765">
                      Payments / Settlements
                    </span>
                    <span className="text-sm font-mono font-medium text-gray-900" data-id="element-8766">
                      {formatMoney(suggestion.payments.reduce((sum, p) => sum + p.grossAmount, 0))}
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-8767">
                    {suggestion.payments.map(p => <div key={p.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200" data-id="element-8768">
                        <div className="flex items-center gap-3" data-id="element-8769">
                          <CreditCardIcon size={16} className="text-gray-400" data-id="element-8770" />
                          <div data-id="element-8771">
                            <div className="text-sm font-medium text-gray-900" data-id="element-8772">
                              {p.reference}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-8773">
                              {p.method}
                            </div>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-8774">
                          <div className="text-sm font-mono text-gray-900" data-id="element-8775">
                            {formatMoney(p.grossAmount)}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-8776">
                            {new Date(p.date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Center: Match Link */}
                <div className="flex items-center justify-center p-4 bg-white relative" data-id="element-8777">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-8778">
                    <div className="w-full h-px bg-gray-200" data-id="element-8779"></div>
                  </div>
                  <div className="relative z-10 flex flex-col items-center gap-2 bg-white px-4 py-2" data-id="element-8780">
                    {getStatusBadge(suggestion.status)}
                    {suggestion.confidenceScore && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getConfidenceColor(suggestion.confidenceScore)}`} data-id="element-8781">
                        {suggestion.confidenceScore}% Match
                      </span>}
                    <ArrowRightIcon size={16} className="text-gray-300" data-id="element-8782" />
                  </div>
                </div>

                {/* Right: Documents */}
                <div className="flex-1 p-5 bg-white" data-id="element-8783">
                  <div className="flex items-center justify-between mb-4" data-id="element-8784">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-8785">
                      Open Documents
                    </span>
                    <span className="text-sm font-mono font-medium text-gray-900" data-id="element-8786">
                      {formatMoney(suggestion.documents.reduce((sum, d) => sum + d.amount, 0))}
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-8787">
                    {suggestion.documents.map(d => <div key={d.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200" data-id="element-8788">
                        <div className="flex items-center gap-3" data-id="element-8789">
                          <FileTextIcon size={16} className="text-gray-400" data-id="element-8790" />
                          <div data-id="element-8791">
                            <div className="text-sm font-medium text-gray-900" data-id="element-8792">
                              {d.reference}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-8793">
                              {d.counterparty}
                            </div>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-8794">
                          <div className="text-sm font-mono text-gray-900" data-id="element-8795">
                            {formatMoney(d.amount)}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-8796">
                            Due {new Date(d.dueDate).toLocaleDateString()}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>)}
        </div>}

      {/* Exceptions Tab */}
      {activeTab === 'exceptions' && <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-8797">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-8798">
            <thead className="bg-gray-50" data-id="element-8799">
              <tr data-id="element-8800">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8801">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8802">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8803">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8804">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8805">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-8806">
              {mockExceptions.map(exc => <tr key={exc.id} className="hover:bg-gray-50" data-id="element-8807">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8808">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-50 text-red-700 text-xs font-medium" data-id="element-8809">
                      <AlertCircleIcon size={14} data-id="element-8810" />
                      {exc.type.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-8811">
                    <div className="text-sm text-gray-900" data-id="element-8812">
                      {exc.description}
                    </div>
                    <div className="text-xs text-gray-500 mt-1" data-id="element-8813">
                      Suggestion: {exc.suggestedResolution}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-8814">
                    {new Date(exc.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-right font-medium text-gray-900" data-id="element-8815">
                    {formatMoney(exc.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8816">
                    <button className="text-blue-600 hover:text-blue-900" data-id="element-8817">
                      Resolve
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      <ReconciliationDrawer suggestion={selectedSuggestion} onClose={() => setSelectedSuggestion(null)} onAccept={handleAccept} data-id="element-8818" />
    </div>;
}